// react
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	CheckCircle as IconCheck,
	Description as IconDoc,
	RadioButtonUnchecked as IconUnchecked,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// services
import { fetchAiBlob, fetchAiDocuments, fetchAiDocumentResults } from '../../../services/aiApi';
import type { AiDocument } from '../../pages/ProjectV2/Documents/documentTypes';
import type { PersonaInsight } from '../../pages/ProjectV2/Documents/documentTypes';

// ui
import { AiDisclaimerBanner } from '../AiDisclaimerBanner/AiDisclaimerBanner';

// Props

interface ContractDocumentsSectionProps {
	contractId: number;
	onDocumentInsightsLoaded: (
		insights: Array<{
			persona: string;
			summary: string;
			keyInformationTable?: Record<string, string>;
			checklist?: string[];
			disbursementConditions?: string;
			timelines?: string;
			responsibleParties?: string;
			keyRisks?: string;
		}> | null
	) => void;
	onInsightsLoadingChange?: (loading: boolean) => void;
}

// Component

export const ContractDocumentsSection = ({
	contractId,
	onDocumentInsightsLoaded,
	onInsightsLoadingChange,
}: ContractDocumentsSectionProps) => {
	const { t } = useTranslation();
	const [docs, setDocs] = useState<AiDocument[]>([]);
	const [loading, setLoading] = useState(false);
	const [insightsLoading, setInsightsLoading] = useState(false);
	const [insightsLoaded, setInsightsLoaded] = useState(false);
	const [insightCount, setInsightCount] = useState(0);
	// IDs of the documents the user has selected to feed the AI analysis.
	// Defaults to every processed document once docs are loaded.
	const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

	// Fetch contract documents
	const loadDocs = useCallback(async () => {
		if (!contractId) return;
		setLoading(true);
		try {
			const response = await fetchAiDocuments({
				tags: `contract:${contractId}`,
				populate: 'uploaded_by,source',
				page_size: 50,
			});
			setDocs(response.data);

			// Default selection: every processed (analyzable) document.
			const processedIds = response.data.filter((d) => d.job_id).map((d) => d.id);
			setSelectedDocIds(new Set(processedIds));
		} catch {
			// silent
		} finally {
			setLoading(false);
		}
	}, [contractId]);

	// Load AI insights from the selected processed documents (parallelized)
	const loadInsights = async (docsWithJobs: AiDocument[]) => {
		setInsightsLoading(true);
		setInsightsLoaded(false);
		onInsightsLoadingChange?.(true);
		try {
			// 1. Fetch all document results in parallel
			const resultsSettled = await Promise.allSettled(
				docsWithJobs
					.filter((d): d is { job_id: string } & typeof d => !!d.job_id)
					.map((doc) => fetchAiDocumentResults(doc.job_id))
			);

			// 2. Collect all gold/pre-gold JSON blobs across all docs
			const blobsToFetch: { blob_name: string; stage: string }[] = [];
			for (const result of resultsSettled) {
				if (result.status !== 'fulfilled' || !result.value.blobs) continue;
				const aiBlobs = result.value.blobs.filter(
					(b) =>
						b.blob_name &&
						b.blob_name.endsWith('.json') &&
						(b.stage === 'gold' || b.stage === 'pre-gold')
				);
				for (const blob of aiBlobs) {
					if (blob.blob_name) {
						blobsToFetch.push({ blob_name: blob.blob_name, stage: blob.stage });
					}
				}
			}

			console.log(
				`[ContractDocs] ${resultsSettled.length} doc results fetched, ${blobsToFetch.length} gold/pre-gold JSON blobs to fetch`
			);

			// 3. Fetch all blob contents in parallel
			const blobSettled = await Promise.allSettled(
				blobsToFetch.map((blob) => {
					const slashIdx = blob.blob_name.indexOf('/');
					const container = slashIdx > 0 ? blob.blob_name.slice(0, slashIdx) : blob.stage || 'gold';
					const blobPath = slashIdx > 0 ? blob.blob_name.slice(slashIdx + 1) : blob.blob_name;
					return fetchAiBlob(container, blobPath).then((res) => ({
						content: res.content,
						blob_name: blob.blob_name,
					}));
				})
			);

			// 4. Parse all blob contents into insights
			const allInsights: PersonaInsight[] = [];
			for (const result of blobSettled) {
				if (result.status !== 'fulfilled') continue;
				const { content, blob_name } = result.value;
				try {
					const parsed = typeof content === 'string' ? JSON.parse(content) : content;
					console.log(
						`[ContractDocs] Blob ${blob_name} parsed:`,
						Array.isArray(parsed) ? `array(${parsed.length})` : typeof parsed
					);
					if (Array.isArray(parsed)) {
						allInsights.push(
							...parsed.map((p: PersonaInsight) => ({
								...p,
								blobName: blob_name ?? undefined,
							}))
						);
					} else if (parsed && typeof parsed === 'object') {
						allInsights.push({
							...(parsed as PersonaInsight),
							blobName: blob_name ?? undefined,
						});
					}
				} catch {
					console.error(`[ContractDocs] Failed to parse blob:`, blob_name);
				}
			}

			console.log(`[ContractDocs] Total insights collected:`, allInsights.length);

			if (allInsights.length > 0) {
				setInsightsLoaded(true);
				setInsightCount(allInsights.length);
				onDocumentInsightsLoaded(allInsights);
			} else {
				onDocumentInsightsLoaded(null);
			}
		} catch {
			onDocumentInsightsLoaded(null);
		} finally {
			setInsightsLoading(false);
			onInsightsLoadingChange?.(false);
		}
	};

	useEffect(() => {
		loadDocs();
	}, [loadDocs]);

	// Reload insights whenever the user's document selection changes.
	// Only processed (job_id) documents that are selected are analyzed.
	useEffect(() => {
		const selected = docs.filter((d) => d.job_id && selectedDocIds.has(d.id));
		if (selected.length === 0) {
			setInsightsLoaded(false);
			setInsightCount(0);
			onDocumentInsightsLoaded(null);
			return;
		}
		loadInsights(selected);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable enough; avoid re-running on prop identity changes
	}, [selectedDocIds, docs]);

	// Toggle a single document in/out of the analysis selection.
	const handleToggleDoc = (docId: string) => {
		setSelectedDocIds((prev) => {
			const next = new Set(prev);
			if (next.has(docId)) {
				next.delete(docId);
			} else {
				next.add(docId);
			}
			return next;
		});
	};

	// Processed documents are the only ones that can be analyzed.
	const processedDocs = docs.filter((d) => d.job_id);
	const allSelected =
		processedDocs.length > 0 && processedDocs.every((d) => selectedDocIds.has(d.id));

	const handleToggleAll = () => {
		if (allSelected) {
			setSelectedDocIds(new Set());
		} else {
			setSelectedDocIds(new Set(processedDocs.map((d) => d.id)));
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
				<CircularProgress size={16} />
				<Typography variant="body2" color="text.secondary">
					Loading contract documents...
				</Typography>
			</Box>
		);
	}

	if (docs.length === 0) {
		return (
			<Paper
				elevation={0}
				sx={{
					bgcolor: alpha('#ff9800', 0.06),
					border: '1px dashed',
					borderColor: alpha('#ff9800', 0.3),
					borderRadius: 2,
					p: 2,
				}}>
				<Stack spacing={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<IconUpload sx={{ fontSize: 20, color: '#ff9800' }} />
						<Typography variant="body2" sx={{ fontWeight: 500, color: '#ff9800' }}>
							{t('spendPlan.conditions.docs.notFound')}
						</Typography>
					</Stack>
					<Typography variant="body2" color="text.secondary">
						{t('spendPlan.conditions.docs.uploadPrompt')}
					</Typography>
				</Stack>
			</Paper>
		);
	}

	return (
		<Stack spacing={1}>
			{/* Shared AI-Assisted Information banner — sets context before the
			   user sees AI-derived document insights or the downstream generator. */}
			<AiDisclaimerBanner />

			{/* Selection header — let the user pick which documents feed the AI */}
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
				<Typography variant="caption" color="text.secondary">
					{t(
						'spendPlan.conditions.docs.selectPrompt',
						'Select the documents to use for AI analysis'
					)}
				</Typography>
				{processedDocs.length > 0 && (
					<Button size="small" onClick={handleToggleAll} sx={{ minWidth: 0, textTransform: 'none' }}>
						{allSelected
							? t('spendPlan.conditions.docs.clearAll', 'Clear all')
							: t('spendPlan.conditions.docs.selectAll', 'Select all')}
					</Button>
				)}
			</Stack>

			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
				{docs.map((doc) => {
					const isProcessed = !!doc.job_id;
					const isSelected = isProcessed && selectedDocIds.has(doc.id);
					const label =
						doc.filename.length > 35 ? `${doc.filename.slice(0, 32)}...` : doc.filename;
					return (
						<Tooltip
							key={doc.id}
							title={
								isProcessed
									? doc.filename
									: `${doc.filename} — ${t(
											'spendPlan.conditions.docs.notProcessed',
											'not yet processed; cannot be analyzed'
										)}`
							}>
							{/* span wrapper keeps the Tooltip working for disabled chips */}
							<span>
								<Chip
									icon={
										isProcessed ? (
											isSelected ? (
												<IconCheck sx={{ fontSize: 16 }} />
											) : (
												<IconUnchecked sx={{ fontSize: 16 }} />
											)
										) : (
											<IconDoc sx={{ fontSize: 16 }} />
										)
									}
									label={label}
									size="small"
									variant={isSelected ? 'filled' : 'outlined'}
									color={isSelected ? 'success' : 'default'}
									onClick={isProcessed ? () => handleToggleDoc(doc.id) : undefined}
									disabled={!isProcessed}
									sx={{
										cursor: isProcessed ? 'pointer' : 'default',
										borderColor: isProcessed ? 'success.main' : 'grey.300',
										color: !isSelected && isProcessed ? 'success.dark' : undefined,
									}}
								/>
							</span>
						</Tooltip>
					);
				})}
			</Box>

			{/* Insights status */}
			{insightsLoading && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<CircularProgress size={14} />
					<Typography variant="caption" color="text.secondary">
						Loading AI insights from documents...
					</Typography>
				</Box>
			)}
			{!insightsLoading && selectedDocIds.size === 0 && processedDocs.length > 0 && (
				<Typography variant="caption" color="text.secondary">
					{t(
						'spendPlan.conditions.docs.noneSelected',
						'No documents selected — select at least one to enable AI analysis.'
					)}
				</Typography>
			)}
			{insightsLoaded && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<IconCheck sx={{ fontSize: 16, color: 'success.main' }} />
					<Typography variant="caption" color="success.dark">
						{insightCount} AI insight{insightCount !== 1 ? 's' : ''} loaded from{' '}
						{selectedDocIds.size} selected document{selectedDocIds.size !== 1 ? 's' : ''}
					</Typography>
				</Box>
			)}
		</Stack>
	);
};
